import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ParticipantEditor } from "@/components/participants/ParticipantEditor";
import type { ContactRecord } from "@/hooks/useContacts";
import { ParticipantDraft, normaliseEmail, ScriptFormParticipantSummary } from "@/types/participants";
import { syncParticipantsForScriptForm } from "@/lib/participants";
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";
import { getErrorMessage } from "@/utils/errors";
import { useTranslation } from "@/i18n/useTranslation";

interface ManageParticipantsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  scriptFormId: string | null;
  formName: string;
  participants: ScriptFormParticipantSummary[];
  contacts: ContactRecord[];
  refreshContacts: () => Promise<void>;
  onParticipantsUpdated: () => Promise<void> | void;
}

export const ManageParticipantsDialog = ({
  open,
  onOpenChange,
  scriptFormId,
  formName,
  participants,
  contacts,
  refreshContacts,
  onParticipantsUpdated,
}: ManageParticipantsDialogProps) => {
  const { toast } = useToast();
  const { t } = useTranslation();
  const [localParticipants, setLocalParticipants] = useState<ParticipantDraft[]>([]);
  const [pendingContacts, setPendingContacts] = useState<ContactRecord[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!open) {
      setLocalParticipants([]);
      setPendingContacts([]);
      return;
    }

    const mapped = participants.map((participant) => {
      const email = normaliseEmail(participant.contacts?.email ?? "");
      return {
        key: `${participant.contact_id}-${participant.id}`,
        contactId: participant.contact_id,
        email,
        name: participant.contacts?.name ?? null,
        isNewContact: false,
      } satisfies ParticipantDraft;
    });

    setLocalParticipants(mapped);
  }, [open, participants]);

  const combinedContacts = useMemo(() => {
    const map = new Map<string, ContactRecord>();
    contacts.forEach((contact) => {
      map.set(normaliseEmail(contact.email), contact);
    });
    pendingContacts.forEach((contact) => {
      const key = normaliseEmail(contact.email);
      if (!map.has(key)) {
        map.set(key, contact);
      }
    });
    return Array.from(map.values());
  }, [contacts, pendingContacts]);

  const handleCreatePendingContact = (contact: ContactRecord) => {
    const key = normaliseEmail(contact.email);
    setPendingContacts((previous) => {
      if (previous.some((existing) => normaliseEmail(existing.email) === key)) {
        return previous;
      }
      if (contacts.some((existing) => normaliseEmail(existing.email) === key)) {
        return previous;
      }
      return [...previous, contact];
    });
  };

  const handleSave = async () => {
    if (!scriptFormId) return;

    setIsSaving(true);
    try {
      await syncParticipantsForScriptForm(
        scriptFormId,
        localParticipants.map((participant) => ({
          contactId: participant.contactId,
          email: participant.email,
          name: participant.name,
        })),
      );

      await refreshContacts();
      await onParticipantsUpdated();

      toast({
        title: t("dashboard.participants.manage.toast_success_title"),
        description: t("dashboard.participants.manage.toast_success_description"),
      });

      onOpenChange(false);
    } catch (error: unknown) {
      console.error("Failed to update participants", error);
      toast({
        title: t("dashboard.participants.manage.toast_error_title"),
        description: getErrorMessage(error, t("dashboard.participants.manage.toast_error_description")),
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("dashboard.participants.manage.title")}</DialogTitle>
          <DialogDescription>
            {t("dashboard.participants.manage.description", { form: formName })}
          </DialogDescription>
        </DialogHeader>

        <ParticipantEditor
          participants={localParticipants}
          onChange={setLocalParticipants}
          availableContacts={combinedContacts}
          onCreatePendingContact={handleCreatePendingContact}
        />

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
            {t("common.cancel")}
          </Button>
          <Button onClick={handleSave} disabled={isSaving || !scriptFormId}>
            {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t("dashboard.participants.manage.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
